import { useEffect, useRef, useState, type RefObject } from 'react'

// Motion that every kit component shares.
//
// # WHY A HOOK AND NOT ONLY THE CSS MEDIA QUERY
//
// The stylesheet already turns animation off under prefers-reduced-motion, but
// a number that counts up is driven from script, one frame at a time, and CSS
// cannot stop that. So script asks the same question, and keeps asking: the
// setting can be changed while the console is open, and a person who switches
// motion off expects the next number to stop moving, not the next page load.

const QUERY = '(prefers-reduced-motion: reduce)'

function prefersReduced(): boolean {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.(QUERY).matches)
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReduced)
  useEffect(() => {
    const mq = window.matchMedia?.(QUERY)
    if (!mq) return
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])
  return reduced
}

// A number that rolls to its value. It starts from zero on first draw and from
// whatever is showing when the value changes later, so an interrupted count
// carries on from where the reader saw it rather than jumping back. With
// reduced motion the value is simply the value.
export function useCountUp(value: number, ms = 600): number {
  const reduced = useReducedMotion()
  const [shown, setShown] = useState(() => (prefersReduced() ? value : 0))
  const current = useRef(shown)

  useEffect(() => {
    if (reduced || ms <= 0) {
      current.current = value
      setShown(value)
      return
    }
    const begin = current.current
    if (begin === value) return
    const start = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ms)
      const eased = 1 - Math.pow(1 - t, 3)
      const v = Math.round(begin + (value - begin) * eased)
      current.current = v
      setShown(v)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value, ms, reduced])

  return shown
}

// Indian digit grouping, so a count of vendors or rupees reads the way the
// people using this product write it: 1,23,456 rather than 123,456.
export function formatIN(n: number): string {
  return n.toLocaleString('en-IN')
}

// # TILT
//
// A card you can open leans toward the pointer, up to a few degrees, and a
// soft light moves across it with the pointer, the way a pane of glass catches
// light when it turns. It is what makes a surface read as an object rather
// than a coloured rectangle, and it is the one piece of motion here that
// follows the hand rather than an event, so its limits are strict:
//
// - Only a fine pointer that can hover. A finger has no hover, and a card that
//   leans after it has already been tapped is a glitch, so touch never tilts.
// - Never under reduced motion, and it stops as soon as that setting is
//   switched on.
// - At most one style write per frame. Pointer events arrive faster than the
//   screen draws, so the latest position waits for the next animation frame.
// - Keyboard focus lifts the card (glass.css) but does not lean it: there is
//   no pointer to lean toward.
//
// The element carries data-tilt while tilt is armed and data-tilt="on" while
// the pointer is over it; glass.css draws the lean and the light from four
// custom properties set here. A data attribute rather than a class, because
// React owns className and would drop a class it did not render.

const FINE = '(hover: hover) and (pointer: fine)'

function finePointer(): boolean {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.(FINE).matches)
}

const TILT_VARS = ['--kit-tilt-x', '--kit-tilt-y', '--kit-glare-x', '--kit-glare-y'] as const

// Arms one element and returns what disarms it. The box is measured when the
// pointer arrives, before the card leans, because a leaning card's bounding
// box changes with the lean, and measuring it every frame would feed the tilt
// back into itself as a wobble. A scroll while over the card re-measures.
function arm(el: HTMLElement, max: number, first?: PointerEvent): () => void {
  el.dataset.tilt = ''
  let box: DOMRect | null = null
  let raf = 0
  let px = 0
  let py = 0

  const paint = () => {
    raf = 0
    if (!box) box = el.getBoundingClientRect()
    if (box.width === 0 || box.height === 0) return
    const fx = Math.min(1, Math.max(0, (px - box.left) / box.width))
    const fy = Math.min(1, Math.max(0, (py - box.top) / box.height))
    // Right of centre turns the right edge away (rotateY positive); below
    // centre turns the bottom edge away (rotateX negative).
    el.style.setProperty('--kit-tilt-y', ((fx - 0.5) * 2 * max).toFixed(2) + 'deg')
    el.style.setProperty('--kit-tilt-x', ((0.5 - fy) * 2 * max).toFixed(2) + 'deg')
    el.style.setProperty('--kit-glare-x', (fx * 100).toFixed(1) + '%')
    el.style.setProperty('--kit-glare-y', (fy * 100).toFixed(1) + '%')
    el.dataset.tilt = 'on'
  }
  const move = (e: PointerEvent) => {
    if (e.pointerType === 'touch') return
    px = e.clientX
    py = e.clientY
    if (!raf) raf = requestAnimationFrame(paint)
  }
  const rest = () => {
    if (raf) cancelAnimationFrame(raf)
    raf = 0
    box = null
    for (const v of TILT_VARS) el.style.removeProperty(v)
    if (el.dataset.tilt !== undefined) el.dataset.tilt = ''
  }
  const remeasure = () => {
    box = null
  }

  el.addEventListener('pointermove', move)
  el.addEventListener('pointerleave', rest)
  el.addEventListener('pointercancel', rest)
  window.addEventListener('scroll', remeasure, { capture: true, passive: true })
  if (first) move(first)

  return () => {
    el.removeEventListener('pointermove', move)
    el.removeEventListener('pointerleave', rest)
    el.removeEventListener('pointercancel', rest)
    window.removeEventListener('scroll', remeasure, { capture: true })
    rest()
    delete el.dataset.tilt
  }
}

// Tilt one element a component renders.
export function useTilt(ref: RefObject<HTMLElement | null>, { max = 5 }: { max?: number } = {}): void {
  const reduced = useReducedMotion()
  useEffect(() => {
    const el = ref.current
    if (!el || reduced || !finePointer()) return
    return arm(el, max)
  }, [ref, max, reduced])
}

// Tilt every element that matches a selector, for cards a screen draws
// without the kit (the exit board's cards). One listener on the document arms
// the card the pointer enters and disarms it when the pointer moves on, so the
// screen's own component is untouched and a card drawn later tilts too.
export function useTiltAll(selector: string, { max = 5 }: { max?: number } = {}): void {
  const reduced = useReducedMotion()
  useEffect(() => {
    if (reduced || !finePointer()) return
    let current: HTMLElement | null = null
    let disarm: (() => void) | null = null
    const over = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      const target = e.target instanceof Element ? e.target.closest<HTMLElement>(selector) : null
      if (target === current) return
      disarm?.()
      current = target
      disarm = target ? arm(target, max, e) : null
    }
    document.addEventListener('pointerover', over)
    return () => {
      document.removeEventListener('pointerover', over)
      disarm?.()
    }
  }, [selector, max, reduced])
}
